import { createApi } from "@reduxjs/toolkit/query/react";
import { baseAuthQuery } from "../base/baseApi";
import { ApiResponse } from "../base/apiResponse";
import { FilterDto, PlanRequest, PlanResponse, PlanWithSubjectsResponse } from "../../models/dtos";

export const plansApi = createApi({
  reducerPath: "plansApi",
  baseQuery: baseAuthQuery("plans"),
  tagTypes: ["Plan"],
  endpoints: (builder) => ({
    getPlans: builder.query<ApiResponse<PlanResponse[]>, Partial<FilterDto>>({
      query: (query) => {
        return {
          url: "",
          method: "POST",
          body: query,
        };
      },
      providesTags: (_result, _error, filter) => {
        if (filter) {
          return [{ type: "Plan", id: JSON.stringify(filter) }];
        }
        return ["Plan"];
      },
    }),
    getPlansAll: builder.query<ApiResponse<PlanResponse[]>, void>({
      query: () => "/getPlans",
      providesTags: ["Plan"],
    }),
    getPlansByFacSpec: builder.query<ApiResponse<PlanResponse[]>, { facultyId: number; specialityId: number }>({
      query: ({ facultyId, specialityId }) => ({
        url: "",
        method: "GET",
        params: { facultyId, specialityId },
      }),
      providesTags: (_result, _error) => {
        return ["Plan"];
      },
    }),
    getPlanById: builder.query<ApiResponse<PlanResponse>, number>({
      query: (id) => `/${id}`,
      providesTags: (_result, _error, id) => {
        return [{ type: "Plan", id: id }];
      },
    }),
    getPlanWithSubjectsById: builder.query<ApiResponse<PlanWithSubjectsResponse>, number>({
      query: (id) => `/${id}/withSubjects`,
      providesTags: (_result, _error, id) => {
        return [{ type: "Plan", id: id }];
      },
    }),
    exportPlan: builder.query<{ blob: Blob; fileName: string }, number>({
      query: (planId) => ({
        url: `export/${planId}`,
        method: "GET",
        responseHandler: async (response) => {
          const blob = await response.blob();
          const contentDisposition = response.headers.get("Content-Disposition") || "";
          let fileName = "";

          const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]*)/i);
          if (utf8Match && utf8Match[1]) {
            fileName = decodeURIComponent(utf8Match[1]);
          } else {
            const match = contentDisposition.match(/filename="([^"]*)"/i);
            if (match && match[1]) {
              fileName = match[1];
            }
          }

          return { blob, fileName };
        },
      }),
    }),
    updatePlan: builder.mutation<ApiResponse<PlanResponse>, Partial<PlanRequest>>({
      query: (body) => ({
        url: "CreateOrUpdate",
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, body) => [{ type: "Plan", id: body.planId }, { type: "Plan" }],
    }),
    createPlanFromCopy: builder.mutation<ApiResponse<number>, number>({
      query: (body) => {
        return {
          url: `CreateFromCopy/${body}`,
          method: "POST",
        };
      },
    }),
    deletePlan: builder.mutation<ApiResponse<boolean>, number>({
      query: (body) => {
        return {
          url: `Delete/${body}`,
          method: "GET",
        };
      },
      invalidatesTags: (_result, _error) => [{ type: "Plan" }],
    }),
  }),
});

export const {
  useGetPlansQuery,
  useGetPlansAllQuery,
  useGetPlansByFacSpecQuery,
  useGetPlanByIdQuery,
  useGetPlanWithSubjectsByIdQuery,
  useLazyExportPlanQuery,
  useUpdatePlanMutation,
  useCreatePlanFromCopyMutation,
  useDeletePlanMutation,
} = plansApi;
